import { useAtom, useSetAtom } from 'jotai';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useCallback } from 'react';

import {
  heidi2OnboardingDevLastBookDemoAtom,
  heidi2OnboardingDevLastOutcomeAtom,
  heidi2OnboardingDevOpenAtom,
} from '@/components/dev/heidi2-onboarding-dev-atoms';
import { useHeidi2WelcomeCtaCopy } from '@/features/flags';
import { getEhrLogoUrl } from '@/helpers/getEhrLogoUrl';
import { useIntegrationEhrInfo } from '@/hooks/integrations/useIntegrationEhrInfo';
import { useUserCanonicalSpecialtyName } from '@/hooks/useUserCanonicalSpecialtyName';
import { heidi2OnboardingTrackers } from '@/models/analytics/features/heidi-2-onboarding-events';
import { AppDirectionProvider } from '@/providers/direction-provider';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import { useHeidi2BookDemoPort } from '@/utils/use-heidi-2-book-demo-port';
import { WORK_CHAT_HOME_PATH } from '@heidi/agents/contracts';
import {
  pendingWorkHomeViewAtom,
  pendingWorkModeAtom,
  useBaseChatHref,
} from '@heidi/agents/data';
import { useMutateActivateHeidi2 } from '@heidi/commerce/data';
import { toast } from '@heidi/design-system/toast';
import { TooltipProvider } from '@heidi/design-system/tooltip';

import type { Heidi2OnboardingOutcome } from '@heidi/agents';

// Lazy so the tour chunk (and its backdrop imagery) only loads once a dev
// actually opens it — this host renders nothing until then.
const Heidi2OnboardingFlow = dynamic(
  () => import('@heidi/agents').then((m) => m.Heidi2OnboardingFlow),
  { ssr: false }
);

export function Heidi2OnboardingDevHost() {
  const [isOpen, setIsOpen] = useAtom(heidi2OnboardingDevOpenAtom);
  const setLastOutcome = useSetAtom(heidi2OnboardingDevLastOutcomeAtom);
  const setLastBookDemo = useSetAtom(heidi2OnboardingDevLastBookDemoAtom);
  const bookDemo = useHeidi2BookDemoPort(setLastBookDemo);
  // The real host's welcome-CTA copy experiment, so the preview shows the arm.
  // Pinned to English: a dev testing an RTL locale still wants to see their arm.
  const welcomeCtaLabel = useHeidi2WelcomeCtaCopy('en');
  // Plays the dev's own specialty script, as the real host does.
  const canonicalSpecialtyName = useUserCanonicalSpecialtyName();
  // And their own EHR's logo on the hero demo's EHR canvas.
  const { integrationEhrProvider } = useIntegrationEhrInfo();
  const ehrLogoSrc = getEhrLogoUrl(integrationEhrProvider);
  const router = useRouter();
  const homeHref = useBaseChatHref();
  const setPendingWorkHomeView = useSetAtom(pendingWorkHomeViewAtom);
  const setPendingWorkMode = useSetAtom(pendingWorkModeAtom);
  const { mutate: activateHeidi2 } = useMutateActivateHeidi2({
    onActivationError: () =>
      toast({ title: 'Heidi II activation failed', tone: 'destructive' }),
  });

  const handleFinish = useCallback(
    (outcome: Heidi2OnboardingOutcome) => {
      setLastOutcome(outcome);
      setIsOpen(false);

      // TODO(heidi2-onboarding): this finish handler is the landing recipe the
      // production host must reuse. That host still does not exist — mount it
      // at the page (not here), persist a once-per-account "seen" flag, fire
      // analytics, and keep this replay path non-persisting.
      //
      // Only a completed run lands on Home. `explore` and `dismissed` are the
      // user opting out of being led anywhere, so moving them would undo that.
      if (outcome !== 'completed') return;

      // The same staging `HomeNavItem` does, and for the same reason: a
      // same-route push doesn't remount the Work page, so Home needs the
      // pending view to reset its sub-view rather than keep the current one.
      if (homeHref === WORK_CHAT_HOME_PATH) {
        setPendingWorkHomeView('home');
        setPendingWorkMode('work');
      }

      void router.push(homeHref);
    },
    [
      homeHref,
      router,
      setIsOpen,
      setLastOutcome,
      setPendingWorkHomeView,
      setPendingWorkMode,
    ]
  );

  if (!isOpen) return null;

  return (
    // DevToolsSidebar mounts above TooltipProvider / AppDirectionProvider in
    // `_app.tsx`, so re-establish both here, the same way the sign-up host
    // does: the design-system tree the tour renders expects them, and Radix
    // direction context keeps RTL faithful when testing RTL locales.
    <AppDirectionProvider>
      <TooltipProvider delayDuration={350}>
        <Heidi2OnboardingFlow
          // Accepting the tour switches Heidi 2 on, the same activation the
          // awareness banner's opt-in modal performs. Real, and not undone by
          // leaving the tour — see the warning in the dev-tools section.
          //
          // Withheld on Apple-compliant builds: activation is a commercial act,
          // so the tour previews without performing one, the same boundary
          // `heidi-2-opt-in-modal-content.tsx` draws with a full suppression.
          onAcceptTour={isAppleCompliantBuild() ? undefined : activateHeidi2}
          onFinish={handleFinish}
          {...heidi2OnboardingTrackers('first_run', welcomeCtaLabel)}
          bookDemo={bookDemo}
          welcomeCtaLabel={welcomeCtaLabel}
          canonicalSpecialtyName={canonicalSpecialtyName}
          ehrLogoSrc={ehrLogoSrc}
        />
      </TooltipProvider>
    </AppDirectionProvider>
  );
}
